import { Link, Navigate, Outlet, useLocation } from 'react-router-dom';

import { useEffect, useState } from 'react';

import { api, clearJwt } from '../api/client.js';

import { AppShell } from '../components/AppShell.jsx';
import { BillingReminderBanner } from '../components/BillingReminderBanner.jsx';
import { SubscriptionOverdueNotice } from '../components/SubscriptionOverdueNotice.jsx';

import { TenantProvider, useTenant } from '../context/TenantContext.jsx';



/** @param {{ user: object, adminOnly?: boolean }} props */

function BillingRemindersGate({ user, adminOnly }) {

  const { tenantId } = useTenant();

  const [billingReminder, setBillingReminder] = useState({ primary: null, extraCount: 0, adminPreview: false });



  useEffect(() => {

    if (!user || adminOnly) {

      setBillingReminder({ primary: null, extraCount: 0, adminPreview: false });

      return undefined;

    }



    const isSuperAdmin = user.role === 'super_admin';

    if (isSuperAdmin && !tenantId) {

      setBillingReminder({ primary: null, extraCount: 0, adminPreview: false });

      return undefined;

    }



    let alive = true;

    (async () => {

      try {

        const path = isSuperAdmin

          ? `/auth/billing-reminders?tenant_id=${encodeURIComponent(String(tenantId))}`

          : '/auth/billing-reminders';

        const res = await api(path);

        if (!alive) return;

        const reminders = Array.isArray(res?.reminders) ? res.reminders : [];

        setBillingReminder({

          primary: res?.primary && typeof res.primary === 'object' ? res.primary : null,

          extraCount: isSuperAdmin ? 0 : Math.max(0, reminders.length - (res?.primary ? 1 : 0)),

          adminPreview: Boolean(isSuperAdmin && res?.preview),

        });

      } catch {

        if (alive) setBillingReminder({ primary: null, extraCount: 0, adminPreview: false });

      }

    })();

    return () => {

      alive = false;

    };

  }, [user, adminOnly, tenantId]);



  if (adminOnly) {

    return null;

  }



  return (

    <BillingReminderBanner

      primary={billingReminder.primary}

      extraCount={billingReminder.extraCount}

      adminPreview={billingReminder.adminPreview}

    />

  );

}



export function ProtectedLayout({ adminOnly }) {

  const loc = useLocation();

  const [state, setState] = useState({

    loading: true,

    user: null,

    error: null,

    trialRedirect: false,

  });



  useEffect(() => {

    let alive = true;

    (async () => {

      try {

        const me = await api('/auth/me');

        if (!alive) return;

        setState({ loading: false, user: me.user, error: null, trialRedirect: false });

      } catch (e) {

        const code = typeof e?.data === 'object' && e?.data ? e.data.code : null;

        if (e?.status === 402 && code === 'TRIAL_EXPIRED') {

          clearJwt();

          if (alive)

            setState({ loading: false, user: null, error: 'trial_expired', trialRedirect: true });

          return;

        }

        if (alive)

          setState({ loading: false, user: null, error: e?.status === 401 ? 'sessão' : 'sessão', trialRedirect: false });

      }

    })();

    return () => {

      alive = false;

    };

  }, []);



  if (state.loading) {

    return (

      <div className="flex min-h-[40vh] items-center justify-center text-sm font-semibold text-ink/60 dark:text-frost-muted">

        Verificando sessão…

      </div>

    );

  }



  if (state.trialRedirect || state.error === 'trial_expired') {

    return <Navigate to="/login?trial=expirado" replace />;

  }



  if (!state.user) {

    return <Navigate to="/login" replace state={{ from: loc.pathname }} />;

  }



  if (adminOnly && state.user.role !== 'super_admin') {

    return <Navigate to="/app/dashboard" replace />;

  }



  return (

    <TenantProvider>

      <AppShell user={state.user} variant={adminOnly ? 'admin' : 'app'}>

        {state.user.billing_plan === 'essential_trial' && state.user.role !== 'super_admin' && !state.user.has_erp_api && (

          <div className="-mt-2 mb-6 rounded-2xl border border-amber-400/50 bg-gradient-to-r from-amber-500/20 to-accent/15 px-4 py-3 text-sm font-medium text-amber-950 backdrop-blur dark:border-amber-300/35 dark:text-amber-50">

            <strong>Plano Essentials (teste)</strong> — Loja pública, pedidos, clientes e produtos liberados. API JSON

            para ERP e token técnico são do{' '}

            <Link className="font-bold underline underline-offset-2" to={{ pathname: '/', hash: 'planos' }}>

              Plano Ouro

            </Link>{' '}

            (veja preços na página inicial).

            {state.user.trial_days_remaining != null ? (

              <span className="mt-2 block font-semibold text-ink dark:text-mist">

                {state.user.trial_days_remaining <= 0

                  ? 'Seu teste expirou — faça login para ver opções de upgrade.'

                  : state.user.trial_days_remaining === 1

                    ? 'Último dia de uso completo incluso antes de precisar de upgrade.'

                    : `Restam aproximadamente ${state.user.trial_days_remaining} dias úteis de teste inclusos.`}

              </span>

            ) : null}

          </div>

        )}

        <BillingRemindersGate user={state.user} adminOnly={adminOnly} />
        <SubscriptionOverdueNotice />

        <Outlet context={{ user: state.user }} />

      </AppShell>

    </TenantProvider>

  );

}

